/**
 * BrandAsset — the shared render half of the brand system.
 *
 * `resolveBrandAsset` decides *what* to render (pure data, unit-tested);
 * this decides *how*. `TenantLogo`, `TenantIcon`, and `TenantBrand` are public
 * wrappers that differ only in which `kind` they ask for, so all three share one
 * implementation of the Butter fallback, sizing, and the scale wrapper.
 *
 * Not exported from `index.ts` — the three named components are the API.
 */

import { ButterLogo } from '#primitives/data-display';
import { resolveBrandAsset, type BrandAssetKind, type BrandSurface } from './resolveBrandAsset';
import { logoSize, type LogoSizeTier } from './sizing';
import { useResolvedTenantHandle } from './useResolvedTenantHandle';

export interface BrandAssetProps {
  tenantHandle?: string;
  kind: BrandAssetKind;
  surface?: BrandSurface;
  /**
   * Size tier. Deliberately has no default here: `TenantIcon` and `TenantBrand`
   * have always fallen back to a fixed legacy class when the caller gives
   * neither `size` nor `className`, and that behaviour has to survive.
   */
  size?: LogoSizeTier;
  /** Used when neither `className` nor `size` is given — each wrapper's legacy default. */
  defaultClassName?: string;
  /** Overrides sizing entirely — wins over `size`, and suppresses the scale wrapper. */
  className?: string;
  ariaHidden?: boolean;
  /** Crop override. Mainly the Playground story; app code uses the registry. */
  viewBox?: string;
  /** Rounding override. Mainly the Playground story. */
  rounded?: boolean;
  /** Scale override. Mainly the Playground story's slider. */
  scale?: number;
}

export function BrandAsset({
  tenantHandle: explicitHandle,
  kind,
  surface = 'light',
  size,
  defaultClassName,
  className,
  ariaHidden = true,
  viewBox,
  rounded,
  scale,
}: BrandAssetProps) {
  const tenantHandle = useResolvedTenantHandle(explicitHandle);

  // The tier only affects sizing, so an absent `size` can resolve at 'lg' and
  // still be ignored below — shape, crop, and rounding don't depend on it.
  const resolved = resolveBrandAsset({ handle: tenantHandle, kind, surface, size: size ?? 'lg' });

  // Explicit props win over the registry (the registry is the app default) —
  // except the crop on an icon, which no caller may override. `logoViewBox`
  // describes the logo; an icon is separate artwork with its own viewBox, and
  // the resolver already withholds the registry value for that reason. Without
  // this guard a `TenantBrand` caller passing `viewBox` (the Playground does)
  // would hand a logo crop straight to a resolved icon and reopen the hole.
  const resolvedViewBox = resolved.layer === 'icon' ? undefined : (viewBox ?? resolved.viewBox);
  const resolvedRounded = rounded ?? resolved.rounded;

  const sizing = scale === undefined ? resolved.sizing : logoSize(size ?? 'lg', resolved.shape, scale);

  // Precedence: explicit className → tier sizing → the wrapper's legacy default.
  const useTierSizing = !className && (size !== undefined || !defaultClassName);
  const scaleStyle = useTierSizing ? sizing.style : undefined;
  // Inside the scale wrapper the mark FITS the box rather than filling it:
  // `max-h-full max-w-full` lets it shrink on whichever axis binds first while
  // keeping its own aspect. `h-full w-auto` looks equivalent and is not — it
  // pins the height while the browser's `img { max-width: 100% }` independently
  // caps the width, which squashed a wide wordmark by ~28% the moment the
  // scaled width exceeded the space available.
  const assetClass =
    className ??
    (scaleStyle ? 'max-h-full max-w-full' : useTierSizing ? sizing.className : defaultClassName!);

  const { Logo } = resolved;

  const content = Logo ? (
    // width/height are left to the component's own defaults; CSS drives the
    // rendered size, and viewBox + CSS handle the SVG scaling.
    <Logo
      className={assetClass}
      ariaHidden={ariaHidden}
      rounded={resolvedRounded}
      viewBox={resolvedViewBox}
    />
  ) : (
    <ButterLogo
      variant={resolved.fallbackVariant}
      tone={resolved.tone}
      className={assetClass}
      ariaHidden={ariaHidden}
    />
  );

  // A scaled tenant needs a computed height, and `LogoProps` has no `style` —
  // adding one would mean threading it through all ~12 logo components for a
  // knob no tenant has set yet. So the height lives on a wrapper that only
  // exists when a scale is actually in play. With no scale (every tenant today)
  // the markup is byte-identical to before.
  if (scaleStyle) {
    return (
      <span className="inline-flex items-center justify-center" style={scaleStyle}>
        {content}
      </span>
    );
  }

  return content;
}
